/**
 * Comentários do Instagram — fila de moderação.
 *
 * Separado do Atendimento de propósito: um comentário é PÚBLICO e está preso a
 * um post, não a uma conversa. Responder aqui aparece para qualquer pessoa que
 * abrir o post, o que é uma decisão diferente de responder no privado — e por
 * isso tem permissão própria.
 *
 * Três ações, e a diferença entre elas importa:
 *  - responder em público: todo mundo vê, é a voz da marca;
 *  - chamar no privado: abre uma DM com quem comentou, para pedir dado pessoal
 *    que não pode ficar num post. A Meta só aceita uma vez, e só por 7 dias;
 *  - ocultar: some do post para os outros, mas o autor continua vendo o próprio
 *    comentário. É reversível. Excluir não existe aqui.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useContextoDeLista } from "@/features/assistente/contexto-de-tela";
import { PermissionGuard } from "@/components/PermissionGuard";
import { usePermissions } from "@/hooks/usePermissions";
import DashboardLayout from "@/components/DashboardLayout";
import { toUserFriendlyError } from "@/lib/error-messages";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Instagram,
  MessageSquare,
  EyeOff,
  Eye,
  Send,
  Lock,
  Check,
  Loader2,
  ExternalLink,
  Inbox as InboxIcon,
} from "lucide-react";
import { toast } from "sonner";
import { tempoRelativo } from "@/lib/tempo";
import { IG_PRIVATE_REPLY_WINDOW_MS } from "@shared/instagram";
import InstagramAccountSelect, {
  useInstagramAccounts,
  rotuloConta,
} from "@/components/integrations/InstagramAccountSelect";

type StatusFiltro = "new" | "replied" | "hidden" | "ignored";

const ABAS: Array<{ value: StatusFiltro; label: string }> = [
  { value: "new", label: "A responder" },
  { value: "replied", label: "Respondidos" },
  { value: "hidden", label: "Ocultos" },
  { value: "ignored", label: "Arquivados" },
];



function InstagramCommentsContent() {
  const utils = trpc.useUtils();
  const { can } = usePermissions();
  const podeResponder = can("instagram:reply_comments");

  const [aba, setAba] = useState<StatusFiltro>("new");
  const [respondendoId, setRespondendoId] = useState<number | null>(null);
  const [texto, setTexto] = useState("");
  const [modoPrivado, setModoPrivado] = useState(false);
  // null = todas as contas do workspace.
  const [contaFiltro, setContaFiltro] = useState<number | null>(null);

  // `options` exige só ver comentários — quem modera não precisa poder mexer
  // na conexão para saber se há conta.
  const contas = useInstagramAccounts();
  const nomeDaConta = new Map((contas.data ?? []).map((c) => [c.id, rotuloConta(c)]));
  const variasContas = (contas.data?.length ?? 0) > 1;

  const contagens = trpc.instagram.comments.counts.useQuery(
    { accountId: contaFiltro ?? undefined },
    { staleTime: 30_000 },
  );

  const comentarios = trpc.instagram.comments.list.useQuery(
    { status: aba, limit: 100, accountId: contaFiltro ?? undefined },
    { staleTime: 15_000, refetchInterval: 60_000 },
  );

  useContextoDeLista({
    titulo: "Comentários do Instagram",
    filtros: { aba },
    total: contagens.data?.[aba] ?? undefined,
    itens: ((comentarios.data as any)?.items ?? (comentarios.data as any) ?? []).map((c: any) => ({
      id: c.id,
      de: c.fromUsername ? `@${c.fromUsername}` : null,
      texto: typeof c.text === "string" ? c.text.slice(0, 200) : null,
      status: c.status,
    })),
    extra: { porAba: contagens.data ?? null },
  });

  const invalidar = () => {
    void utils.instagram.comments.list.invalidate();
    void utils.instagram.comments.counts.invalidate();
  };

  const fecharComposer = () => {
    setRespondendoId(null);
    setTexto("");
    setModoPrivado(false);
  };

  const responder = trpc.instagram.comments.reply.useMutation({
    onSuccess: () => {
      fecharComposer();
      invalidar();
      toast.success("Resposta publicada no post.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const responderPrivado = trpc.instagram.comments.privateReply.useMutation({
    onSuccess: () => {
      fecharComposer();
      invalidar();
      toast.success("Mensagem enviada no privado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const ocultar = trpc.instagram.comments.setHidden.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Comentário atualizado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const arquivar = trpc.instagram.comments.ignore.useMutation({
    onSuccess: () => {
      invalidar();
      toast.success("Comentário arquivado.");
    },
    onError: (err) => toast.error(toUserFriendlyError(err)),
  });

  const enviando = responder.isPending || responderPrivado.isPending;

  // Sem conta conectada não há o que moderar. `conta.data === null` só é
  // confiável para quem tem permissão de ver a credencial; para os demais a
  // lista vazia já diz o suficiente.
  const semConta = contas.isFetched && (contas.data?.length ?? 0) === 0;

  if (semConta) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center">
        <Instagram className="mx-auto h-10 w-10 text-muted-foreground" />
        <h2 className="mt-4 text-lg font-semibold">Instagram não conectado</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Conecte a conta em <strong>Configurações → Integrações → Instagram</strong> para os
          comentários dos seus posts aparecerem aqui.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Comentários do Instagram</h1>
          <p className="text-sm text-muted-foreground">
            O que as pessoas escreveram nos seus posts. Responder aqui é público.
          </p>
        </div>
        <InstagramAccountSelect
          value={contaFiltro}
          onChange={(id) => {
            setContaFiltro(id);
            fecharComposer();
          }}
          permitirTodas
        />
      </div>

      <div className="flex flex-wrap gap-2">
        {ABAS.map((a) => {
          const n = contagens.data?.[a.value] ?? 0;
          return (
            <Button
              key={a.value}
              variant={aba === a.value ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setAba(a.value);
                fecharComposer();
              }}
            >
              {a.label}
              {n > 0 && (
                <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-[10px]">
                  {n}
                </Badge>
              )}
            </Button>
          );
        })}
      </div>

      {comentarios.isLoading && (
        <p className="text-sm text-muted-foreground">Carregando comentários…</p>
      )}
      {comentarios.isError && (
        <p className="text-sm text-red-600">
          Não consegui carregar: {toUserFriendlyError(comentarios.error)}
        </p>
      )}
      {comentarios.data?.length === 0 && !comentarios.isLoading && (
        <div className="rounded border border-dashed py-12 text-center">
          <MessageSquare className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            {aba === "new" ? "Nenhum comentário esperando resposta." : "Nada por aqui."}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {comentarios.data?.map((c) => {
          const feitoEm = c.commentedAt ? new Date(c.commentedAt) : null;
          const privadoExpirou =
            !!feitoEm && Date.now() - feitoEm.getTime() > IG_PRIVATE_REPLY_WINDOW_MS;
          const aberto = respondendoId === c.id;

          return (
            <Card key={c.id}>
              <CardContent className="space-y-3 pt-5">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">
                    {c.fromUsername ? `@${c.fromUsername}` : "Alguém"}
                  </span>
                  <span className="text-xs text-muted-foreground">{tempoRelativo(c.commentedAt)}</span>
                  {variasContas && contaFiltro == null && nomeDaConta.get(c.instagramAccountId) && (
                    <Badge variant="outline" className="text-[10px]" title="Conta que recebeu o comentário">
                      {nomeDaConta.get(c.instagramAccountId)}
                    </Badge>
                  )}
                  {c.parentCommentId && (
                    <Badge variant="outline" className="text-[10px]">
                      resposta
                    </Badge>
                  )}
                  {c.status === "hidden" && (
                    <Badge className="bg-slate-200 text-slate-700 text-[10px]">Oculto</Badge>
                  )}
                  {c.contactId && (
                    <Badge variant="outline" className="gap-1 text-[10px]">
                      <InboxIcon className="h-3 w-3" />
                      tem conversa
                    </Badge>
                  )}
                  {c.mediaPermalink && (
                    <a
                      href={c.mediaPermalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      ver post <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>

                <p className="whitespace-pre-wrap text-sm">{c.text || "(sem texto)"}</p>

                {c.status === "replied" && c.replyText && (
                  <div className="rounded border-l-2 border-emerald-400 bg-emerald-50 py-2 pl-3 text-sm dark:bg-emerald-950/30">
                    <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">
                      Sua resposta
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap">{c.replyText}</p>
                  </div>
                )}

                {podeResponder && (
                  <>
                    {!aberto ? (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRespondendoId(c.id);
                            setModoPrivado(false);
                          }}
                        >
                          <MessageSquare className="mr-2 h-4 w-4" />
                          Responder no post
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={privadoExpirou}
                          title={
                            privadoExpirou
                              ? "A Meta só aceita resposta privada até 7 dias depois do comentário"
                              : undefined
                          }
                          onClick={() => {
                            setRespondendoId(c.id);
                            setModoPrivado(true);
                          }}
                        >
                          <Lock className="mr-2 h-4 w-4" />
                          Chamar no privado
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            ocultar.mutate({ id: c.id, hidden: c.status !== "hidden" })
                          }
                          disabled={ocultar.isPending}
                        >
                          {c.status === "hidden" ? (
                            <>
                              <Eye className="mr-2 h-4 w-4" /> Reexibir
                            </>
                          ) : (
                            <>
                              <EyeOff className="mr-2 h-4 w-4" /> Ocultar
                            </>
                          )}
                        </Button>
                        {c.status === "new" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => arquivar.mutate({ id: c.id })}
                            disabled={arquivar.isPending}
                          >
                            <Check className="mr-2 h-4 w-4" />
                            Arquivar
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div
                          className={`rounded border p-2 text-xs ${
                            modoPrivado
                              ? "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200"
                              : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                          }`}
                        >
                          {modoPrivado
                            ? "Vai como mensagem direta, só para quem comentou. A Meta aceita uma vez por comentário."
                            : "Vai como resposta pública no post — qualquer pessoa consegue ler."}
                        </div>
                        <Textarea
                          value={texto}
                          onChange={(e) => setTexto(e.target.value)}
                          placeholder={modoPrivado ? "Mensagem privada…" : "Resposta pública…"}
                          rows={3}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={!texto.trim() || enviando}
                            onClick={() =>
                              modoPrivado
                                ? responderPrivado.mutate({ id: c.id, message: texto.trim() })
                                : responder.mutate({ id: c.id, message: texto.trim() })
                            }
                          >
                            {enviando ? (
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                              <Send className="mr-2 h-4 w-4" />
                            )}
                            {modoPrivado ? "Enviar no privado" : "Publicar resposta"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={fecharComposer}>
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default function InstagramComments() {
  return (
    <PermissionGuard permission="instagram:view_comments">
      <DashboardLayout>
        <InstagramCommentsContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
